// LUMINA · pages/vajra/admin/TeachingsSection.jsx
// Phase 39D+72: 法教管理 + 阶段管理 + 资料管理

import React, { useState, useEffect } from 'react';
import { API, GD, GDL, TXT, TXT2, PANEL } from '../constants';
import { VajraBtn } from '../primitives';

const BLANK_TEACHING = {title_zh:"",title_en:"",description_zh:"",description_en:"",teaching_type:"teaching",teacher_id:"",tradition:"",lineage:"",credential_key:"",cover_image_url:"",full_description:"",prerequisite_zh:"",materials_note:"",is_public:true,topic_hub_slug:""};
const BLANK_STAGE = {stage_order:1,name_zh:"",name_en:"",description_zh:"",credential_key:"",is_required:true};
const BLANK_RESOURCE = {title_zh:"",file_type:"pdf",file_url:"",description:"",category:"",language:"zh",stage_id:""};

const FILE_ICON = { pdf:"📄", audio:"🎵", video:"🎬", image:"🖼", docx:"📝", link:"🔗", text:"📋" };
const FILE_LABEL = { pdf:"PDF", audio:"音频", video:"视频", image:"图片", docx:"DOCX", link:"链接", text:"文本" };

export default function TeachingsSection({ token, headers, teacherList, teachingList, setTeachingList, hubList = [] }) {
  const [teachingForm, setTeachingForm] = useState(BLANK_TEACHING);
  const [editTeachingId, setEditTeachingId] = useState(null);
  const [teachingMsg, setTeachingMsg] = useState("");
  const [activeStageTeachingId, setActiveStageTeachingId] = useState(null);
  const [editStageId, setEditStageId] = useState(null);
  const [stageForm, setStageForm] = useState(BLANK_STAGE);
  const [stageMsg, setStageMsg] = useState("");

  // Phase 72: Resource management state
  const [activeResTeachingId, setActiveResTeachingId] = useState(null);
  const [resourceData, setResourceData] = useState({});  // { [teachingId]: resources[] }
  const [resourceForm, setResourceForm] = useState(BLANK_RESOURCE);
  const [editResourceId, setEditResourceId] = useState(null);
  const [resourceMsg, setResourceMsg] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  const loadTeachings = async () => {
    const d = await fetch(`${API}/vajra/teachings`).then(r=>r.ok?r.json():[]).catch(()=>[]);
    setTeachingList(Array.isArray(d)?d:[]);
  };

  const loadResources = async (teachingId) => {
    const d = await fetch(`${API}/vajra/staff/teachings/${teachingId}/resources`, { headers })
      .then(r => r.ok ? r.json() : []).catch(() => []);
    setResourceData(prev => ({ ...prev, [teachingId]: Array.isArray(d) ? d : [] }));
  };

  // Auto-load resources for all teachings when teachingList changes
  useEffect(() => {
    teachingList.forEach(t => {
      if (!resourceData[t.id]) {
        loadResources(t.id);
      }
    });
  }, [teachingList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { setResourceMsg("✗ 文件过大（最大50MB）"); return; }
    setUploadingImg(true);
    const fd = new FormData();
    fd.append('file', file);
    // Strip Content-Type so browser sets multipart boundary
    const uploadHeaders = { Authorization: headers.Authorization };
    try {
      const r = await fetch(`${API}/vajra/staff/upload`, { method:'POST', headers: uploadHeaders, body: fd });
      const d = await r.json();
      if (r.ok) {
        // auto-detect file_type from response or content-type
        const ft = d.file_type === 'image' ? 'image' :
                   d.file_type === 'pdf' ? 'pdf' :
                   d.file_type === 'mp3' || d.file_type === 'wav' || d.file_type === 'ogg' || d.file_type === 'm4a' ? 'audio' :
                   file.type?.startsWith('audio/') ? 'audio' : 'pdf';
        setResourceForm(f => ({ ...f, file_url: d.url, file_type: ft }));
        setResourceMsg("✓ 文件已上传");
      }
      else setResourceMsg("✗ " + (d.detail || "上传失败"));
    } catch { setResourceMsg("✗ 上传失败"); }
    setUploadingImg(false);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setTeachingMsg("✗ 图片过大（最大10MB）"); return; }
    setCoverUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    const uploadHeaders = { Authorization: headers.Authorization };
    try {
      const r = await fetch(`${API}/vajra/staff/upload`, { method:'POST', headers: uploadHeaders, body: fd });
      const d = await r.json();
      if (r.ok) { setTeachingForm(f => ({ ...f, cover_image_url: d.url })); setTeachingMsg("✓ 封面上传成功"); }
      else setTeachingMsg("✗ " + (d.detail || "上传失败"));
    } catch { setTeachingMsg("✗ 上传失败"); }
    setCoverUploading(false);
  };

  const resourceActions = (teachingId) => (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(212,175,55,0.12)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: GDL, letterSpacing: 1, fontWeight: 600 }}>
          资料 ({(resourceData[teachingId] || []).length})
        </div>
        <button onClick={() => {
          setActiveResTeachingId(activeResTeachingId === teachingId ? null : teachingId);
          setEditResourceId(null);
          setResourceForm(BLANK_RESOURCE);
          setResourceMsg("");
          if (activeResTeachingId !== teachingId) loadResources(teachingId);
        }} style={{ fontSize: 11, padding: "2px 10px", borderRadius: 12, cursor: "pointer", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)", color: "#3b82f6" }}>
          + 添加资料
        </button>
      </div>

      {/* Resource list */}
      {(resourceData[teachingId] || []).map((r, ri) => (
        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 0", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
          <span style={{ fontSize: 13 }}>{FILE_ICON[r.file_type] || "📎"}</span>
          {editResourceId === r.id ? (
            <input autoFocus value={resourceForm.title_zh} onChange={e => setResourceForm(f => ({ ...f, title_zh: e.target.value }))}
              style={{ flex: 1, fontSize: 12, padding: "3px 7px", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 5, background: "#fff" }} />
          ) : (
            <span style={{ flex: 1, fontSize: 12, color: TXT }}>
              {r.title_zh}
              <span style={{ fontSize: 10, color: "#aaa", marginLeft: 6 }}>{FILE_LABEL[r.file_type] || r.file_type}</span>
            </span>
          )}
          <div style={{ display: "flex", gap: 3 }}>
            {editResourceId === r.id ? (
              <button onClick={async () => {
                const payload = { ...resourceForm };
                if (payload.stage_id === "" || payload.stage_id === null || payload.stage_id === undefined) {
                  delete payload.stage_id;
                }
                const r2 = await fetch(`${API}/vajra/staff/resources/${r.id}`, {
                  method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload)
                });
                if (r2.ok) { setResourceMsg("✓ 已保存"); setEditResourceId(null); loadResources(teachingId); }
                else { const d = await r2.json(); setResourceMsg("✗ " + (d.detail || "保存失败")); }
              }} style={{ fontSize: 11, padding: "1px 7px", borderRadius: 4, cursor: "pointer", background: "rgba(5,150,105,0.08)", border: "1px solid rgba(5,150,105,0.25)", color: "#059669" }}>保存</button>
            ) : (
              <button onClick={() => {
                setEditResourceId(r.id);
                setResourceForm({ title_zh: r.title_zh || "", file_type: r.file_type || "pdf", file_url: r.file_url || "", description: r.description || "", category: r.category || "", language: r.language || "zh", stage_id: r.stage_id || "", title_en: r.title_en || "" });
                if (activeResTeachingId !== teachingId) { setActiveResTeachingId(teachingId); loadResources(teachingId); }
              }} style={{ fontSize: 11, padding: "1px 5px", borderRadius: 4, cursor: "pointer", background: "none", border: "1px solid #ddd", color: "#888" }}>✎</button>
            )}
            <button onClick={async () => {
              if (!window.confirm(`删除资料"${r.title_zh}"？`)) return;
              const r2 = await fetch(`${API}/vajra/staff/resources/${r.id}`, { method: 'DELETE', headers });
              if (r2.ok) { setResourceMsg("✓ 已删除"); loadResources(teachingId); }
            }} style={{ fontSize: 11, padding: "1px 5px", borderRadius: 4, cursor: "pointer", background: "none", border: "1px solid #fca5a5", color: "#dc2626" }}>×</button>
          </div>
        </div>
      ))}

      {/* Add/Edit resource form */}
      {activeResTeachingId === teachingId && (
        <div style={{ marginTop: 8, padding: "10px 12px", background: "rgba(59,130,246,0.03)", borderRadius: 8, border: "1px solid rgba(59,130,246,0.12)" }}>
          {resourceMsg && <div style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, marginBottom: 6, background: resourceMsg.startsWith("✗") ? "rgba(220,38,38,0.06)" : "rgba(5,150,105,0.06)", color: resourceMsg.startsWith("✗") ? "#dc2626" : "#059669" }}>{resourceMsg}</div>}
          <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, marginBottom: 6 }}>
            {editResourceId ? "编辑资料" : "添加资料"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
            <input placeholder="资料名称（中文）*" value={resourceForm.title_zh}
              onChange={e => setResourceForm(f => ({ ...f, title_zh: e.target.value }))}
              style={{ padding: "5px 8px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 6, fontSize: 12, background: "#fff" }} />
            <select value={resourceForm.file_type}
              onChange={e => setResourceForm(f => ({ ...f, file_type: e.target.value }))}
              style={{ padding: "5px 8px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 6, fontSize: 12, background: "#fff" }}>
              <option value="pdf">📄 PDF 法本</option>
              <option value="docx">📝 Word 文档</option>
              <option value="audio">🎧 音频 录音 (建议OneDrive链接)</option>
              <option value="video">🎬 视频 (建议OneDrive链接)</option>
              <option value="image">🖼 图片</option>
              <option value="text">📋 纯文本</option>
              <option value="link">🔗 链接</option>
            </select>
          </div>
          {/* File URL row */}
          <div style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center", flexWrap: "wrap" }}>
            <input placeholder="文件URL（https://...）*" value={resourceForm.file_url}
              onChange={e => setResourceForm(f => ({ ...f, file_url: e.target.value }))}
              style={{ flex: "1 1 200px", padding: "5px 8px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 6, fontSize: 12, background: "#fff" }} />
            <label style={{ fontSize: 11, color: "#3b82f6", cursor: uploadingImg ? "wait" : "pointer", padding: "4px 10px", borderRadius: 6, border: "1px dashed rgba(59,130,246,0.3)", background: "rgba(59,130,246,0.04)", whiteSpace: "nowrap", opacity: uploadingImg ? 0.5 : 1 }}>
              {uploadingImg ? "⏳ 上传中…" : "📤 上传文件"}
              <input type="file" accept="image/*,audio/*,.pdf,.txt,.md,.doc,.docx" onChange={handleFileUpload} style={{ display: "none" }} disabled={uploadingImg} />
            </label>
          </div>
          <input placeholder="简介（选填）" value={resourceForm.description}
            onChange={e => setResourceForm(f => ({ ...f, description: e.target.value }))}
            style={{ width: "100%", padding: "5px 8px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 6, fontSize: 12, background: "#fff", marginBottom: 6, boxSizing: "border-box" }} />
          <select value={resourceForm.stage_id || ""}
            onChange={e => setResourceForm(f => ({ ...f, stage_id: e.target.value ? parseInt(e.target.value) : "" }))}
            style={{ width: "100%", padding: "5px 8px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 6, fontSize: 12, background: "#fff", marginBottom: 6, boxSizing: "border-box", color: resourceForm.stage_id ? "#000" : "#999" }}>
            <option value="">关联阶段（选填）</option>
            {(t.stages || []).map(s => (
              <option key={s.id} value={s.id}>{String(s.stage_order).padStart(2,'0')}. {s.name_zh}</option>
            ))}
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={async () => {
              if (!resourceForm.title_zh.trim()) { setResourceMsg("✗ 请填写资料名称"); return; }
              if (!resourceForm.file_url.trim()) { setResourceMsg("✗ 请填写文件URL"); return; }
              const payload = { ...resourceForm, teaching_id: teachingId };
              if (payload.stage_id === "" || payload.stage_id === null || payload.stage_id === undefined) {
                delete payload.stage_id;  // send null as omitted so DB keeps existing value
              }
              const isUpdate = !!editResourceId;
              const url = isUpdate ? `${API}/vajra/staff/resources/${editResourceId}` : `${API}/vajra/staff/resources`;
              const method = isUpdate ? 'PUT' : 'POST';
              const r = await fetch(url, {
                method, headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              if (r.ok) {
                setResourceMsg(isUpdate ? "✓ 资料已更新" : "✓ 资料已添加");
                setEditResourceId(null);
                setResourceForm(BLANK_RESOURCE);
                loadResources(teachingId);
                loadTeachings();
              } else {
                const d = await r.json();
                setResourceMsg("✗ " + (d.detail || "操作失败"));
              }
            }} style={{ padding: "4px 14px", borderRadius: 8, cursor: "pointer", background: "#3b82f6", color: "#fff", border: "none", fontSize: 12 }}>
              {editResourceId ? "更新资料" : "保存资料"}
            </button>
            <button onClick={() => { setActiveResTeachingId(null); setResourceMsg(""); }}
              style={{ padding: "4px 10px", borderRadius: 8, cursor: "pointer", background: "none", border: "1px solid #ddd", color: "#888", fontSize: 12 }}>
              取消
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{background:PANEL,borderRadius:12,padding:"20px",border:`1px solid rgba(0,0,0,0.06)`}}>
      <div style={{fontSize:14,color:GD,marginBottom:4}}>法教管理</div>
      <div style={{fontSize:11,color:"#aaa",marginBottom:16}}>建立法教档案，关联上师与传承。弟子修行资质记录可链接到此处。</div>
      {teachingMsg&&<div style={{fontSize:13,padding:"8px 12px",borderRadius:8,marginBottom:12,background:teachingMsg.startsWith("✗")?"rgba(220,38,38,0.08)":"rgba(5,150,105,0.08)",color:teachingMsg.startsWith("✗")?"#dc2626":"#059669"}}>{teachingMsg}</div>}

      {/* Teaching form */}
      <form onSubmit={async e=>{
        e.preventDefault();
        const payload = {...teachingForm, teacher_id: teachingForm.teacher_id?parseInt(teachingForm.teacher_id):null };
        const url = editTeachingId ? `${API}/vajra/staff/teachings/${editTeachingId}` : `${API}/vajra/staff/teachings`;
        const method = editTeachingId ? "PUT" : "POST";
        const r = await fetch(url,{method,headers:{...headers,"Content-Type":"application/json"},body:JSON.stringify(payload)});
        if(r.ok){ setTeachingMsg(editTeachingId?"✓ 法教已更新":"✓ 法教已添加"); setEditTeachingId(null); setTeachingForm(BLANK_TEACHING); loadTeachings(); }
        else { const d=await r.json(); setTeachingMsg("✗ "+(d.detail||"操作失败")); }
      }} style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20,padding:"14px 16px",background:"rgba(212,175,55,0.04)",borderRadius:10,border:`1px solid rgba(212,175,55,0.15)`}}>
        <div style={{gridColumn:"1/-1",fontSize:12,color:GD,fontWeight:600,marginBottom:4}}>{editTeachingId?"编辑法教":"添加法教"}</div>
        <div>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>法教名称（中文）*</label>
          <input value={teachingForm.title_zh} onChange={e=>setTeachingForm({...teachingForm,title_zh:e.target.value})} required
            placeholder="如：那若六法"
            style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff",boxSizing:"border-box"}}/>
        </div>
        <div>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>法教名称（英文）</label>
          <input value={teachingForm.title_en||""} onChange={e=>setTeachingForm({...teachingForm,title_en:e.target.value})}
            placeholder="如：Six Yogas of Naropa"
            style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff",boxSizing:"border-box"}}/>
        </div>
        <div>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>类型 TYPE</label>
          <select value={teachingForm.teaching_type} onChange={e=>setTeachingForm({...teachingForm,teaching_type:e.target.value})}
            style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff"}}>
            <option value="teaching">教授 Teaching</option>
            <option value="empowerment">灌顶 Empowerment</option>
            <option value="retreat">闭关 Retreat</option>
            <option value="practice">修法 Practice</option>
            <option value="text_study">传文 Text Study</option>
          </select>
        </div>
        <div>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>关联上师 TEACHER</label>
          <select value={teachingForm.teacher_id||""} onChange={e=>setTeachingForm({...teachingForm,teacher_id:e.target.value})}
            style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff"}}>
            <option value="">-- 选择上师（选填）--</option>
            {teacherList.map(t=><option key={t.id} value={t.id}>{t.name_zh}{t.name_en?` / ${t.name_en}`:""}</option>)}
          </select>
        </div>
        {[["tradition","门派","噶举 / 宁玛 / 格鲁"],["lineage","传承","止贡 / 白玉 / 竹巴"],["credential_key","资质 Key（选填）","如：naropa_6yogas"]].map(([k,label,ph])=>(
          <div key={k}>
            <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>{label}</label>
            <input value={teachingForm[k]||""} onChange={e=>setTeachingForm({...teachingForm,[k]:e.target.value})} placeholder={ph}
              style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff",boxSizing:"border-box"}}/>
          </div>
        ))}
        <div style={{gridColumn:"1/-1"}}>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>简介（中文）</label>
          <textarea value={teachingForm.description_zh||""} onChange={e=>setTeachingForm({...teachingForm,description_zh:e.target.value})}
            placeholder="法教简介..." rows={3}
            style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff",boxSizing:"border-box",resize:"vertical"}}/>
        </div>
        {/* Phase 40 F3E: new fields */}
        <div style={{gridColumn:"1/-1"}}>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>完整介绍（详情页显示）</label>
          <textarea value={teachingForm.full_description||""} onChange={e=>setTeachingForm({...teachingForm,full_description:e.target.value})}
            placeholder="完整法教介绍，支持换行..." rows={4}
            style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff",boxSizing:"border-box",resize:"vertical"}}/>
        </div>
        <div>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>封面图 URL</label>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <input value={teachingForm.cover_image_url||""} onChange={e=>setTeachingForm({...teachingForm,cover_image_url:e.target.value})}
              placeholder="https://...  或点右侧上传"
              style={{flex:1,padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff",boxSizing:"border-box"}}/>
            <label style={{fontSize:11,color:GD,cursor:coverUploading?"wait":"pointer",padding:"6px 10px",borderRadius:7,border:"1px dashed rgba(139,105,20,0.4)",background:"rgba(139,105,20,0.04)",whiteSpace:"nowrap",flexShrink:0,opacity:coverUploading?0.5:1}}>
              {coverUploading?"⏳":"🖼 上传"}
              <input type="file" accept="image/*" onChange={handleCoverUpload} style={{display:"none"}} disabled={coverUploading}/>
            </label>
          </div>
        </div>
        <div>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>修习要求</label>
          <input value={teachingForm.prerequisite_zh||""} onChange={e=>setTeachingForm({...teachingForm,prerequisite_zh:e.target.value})}
            placeholder="如：需具足皈依" style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff",boxSizing:"border-box"}}/>
        </div>
        <div>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>材料说明</label>
          <input value={teachingForm.materials_note||""} onChange={e=>setTeachingForm({...teachingForm,materials_note:e.target.value})}
            placeholder="修法材料分发说明..." style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff",boxSizing:"border-box"}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#666",cursor:"pointer"}}>
            <input type="checkbox" checked={!!teachingForm.is_public} onChange={e=>setTeachingForm({...teachingForm,is_public:e.target.checked})}/>
            公开详情页（无需登录可访问）
          </label>
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <label style={{fontSize:10,color:"#aaa",letterSpacing:1,display:"block",marginBottom:3}}>关联专题</label>
          <select value={teachingForm.topic_hub_slug||""} onChange={e=>setTeachingForm({...teachingForm,topic_hub_slug:e.target.value||null})}
            style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:12,background:"#fff",boxSizing:"border-box"}}>
            <option value="">— 无关联专题 —</option>
            {hubList.map(h=><option key={h.slug} value={h.slug}>{h.title_zh} ({h.slug})</option>)}
          </select>
        </div>
        <div style={{gridColumn:"1/-1",display:"flex",gap:8}}>
          <VajraBtn variant="green" type="submit" style={{flex:1}}>{editTeachingId?"保存修改":"添加法教"}</VajraBtn>
          {editTeachingId&&<VajraBtn variant="outline" onClick={()=>{setEditTeachingId(null);setTeachingForm(BLANK_TEACHING);}}>取消</VajraBtn>}
        </div>
      </form>

      {/* Teaching list */}
      {teachingList.length === 0 ? (
        <div style={{fontSize:13,color:"#ccc",textAlign:"center",padding:"20px 0"}}>暂无法教记录</div>
      ) : teachingList.map(t=>(
        <div key={t.id} style={{padding:"12px 14px",marginBottom:8,borderRadius:10,
          background:"rgba(255,255,255,0.7)",border:`1px solid rgba(212,175,55,0.15)`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:600,fontSize:14,color:TXT}}>
                {t.title_zh}
                {t.title_en&&<span style={{fontSize:12,color:TXT2,marginLeft:6}}>{t.title_en}</span>}
              </div>
              <div style={{fontSize:12,color:TXT2,marginTop:3,display:"flex",gap:6,flexWrap:"wrap"}}>
                {[{v:t.teaching_type,colors:["rgba(99,102,241,0.1)","rgba(99,102,241,0.3)"]},
                  {v:t.teacher_name,colors:["rgba(212,175,55,0.1)","rgba(212,175,55,0.3)"]},
                  {v:t.tradition,colors:["rgba(5,150,105,0.08)","rgba(5,150,105,0.25)"]},
                  {v:t.lineage,colors:["rgba(139,105,20,0.06)","rgba(139,105,20,0.2)"]},
                ].filter(x=>x.v).map((x,i)=>(
                  <span key={i} style={{background:x.colors[0],border:`1px solid ${x.colors[1]}`,borderRadius:8,padding:"1px 8px",fontSize:11}}>{x.v}</span>
                ))}
                {t.credential_key&&<span style={{background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.2)",borderRadius:8,padding:"1px 8px",fontSize:11}}>🔑 {t.credential_key}</span>}
              </div>
              {t.description_zh&&<div style={{fontSize:12,color:"#888",marginTop:4,lineHeight:1.5,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{t.description_zh}</div>}
            </div>
            <div style={{display:"flex",gap:6,flexShrink:0,marginLeft:10}}>
              <VajraBtn small variant="outline" onClick={()=>{ setEditTeachingId(t.id); setTeachingForm({title_zh:t.title_zh||"",title_en:t.title_en||"",description_zh:t.description_zh||"",description_en:t.description_en||"",teaching_type:t.teaching_type||"teaching",teacher_id:t.teacher_id?String(t.teacher_id):"",tradition:t.tradition||"",lineage:t.lineage||"",credential_key:t.credential_key||"",cover_image_url:t.cover_image_url||"",full_description:t.full_description||"",prerequisite_zh:t.prerequisite_zh||"",materials_note:t.materials_note||"",is_public:t.is_public!==false,topic_hub_slug:t.topic_hub_slug||""}); window.scrollTo(0,0); }}>编辑</VajraBtn>
              <VajraBtn small variant="outline" onClick={async()=>{ if(!window.confirm(`确认删除 ${t.title_zh}？`)) return; const r=await fetch(`${API}/vajra/staff/teachings/${t.id}`,{method:"DELETE",headers}); if(r.ok){setTeachingMsg("✓ 已删除");loadTeachings();} }} style={{color:"#e57373",borderColor:"#e57373"}}>删除</VajraBtn>
            </div>
          </div>
          {/* Phase 31: stage management */}
          <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(212,175,55,0.12)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontSize:11,color:GDL,letterSpacing:1,fontWeight:600}}>修行阶段 ({(t.stages||[]).length})</div>
              <button onClick={()=>{setActiveStageTeachingId(activeStageTeachingId===t.id?null:t.id);setEditStageId(null);setStageForm({...BLANK_STAGE,stage_order:(t.stages||[]).length+1});setStageMsg("");}} style={{fontSize:11,padding:"2px 10px",borderRadius:12,cursor:"pointer",background:"rgba(212,175,55,0.08)",border:"1px solid rgba(212,175,55,0.25)",color:GD}}>+ 添加阶段</button>
            </div>
            {stageMsg&&activeStageTeachingId===t.id&&<div style={{fontSize:12,padding:"4px 8px",borderRadius:6,marginBottom:6,background:stageMsg.startsWith("✗")?"rgba(220,38,38,0.06)":"rgba(5,150,105,0.06)",color:stageMsg.startsWith("✗")?"#dc2626":"#059669"}}>{stageMsg}</div>}
            {(t.stages||[]).map((s,si)=>(
              <div key={s.id} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 0",borderBottom:"1px solid rgba(0,0,0,0.04)"}}>
                <span style={{fontSize:12,color:TXT2,minWidth:20,textAlign:"right"}}>{"①②③④⑤⑥⑦⑧⑨⑩"[si]||si+1}</span>
                {editStageId===s.id?(
                  <input autoFocus value={stageForm.name_zh} onChange={e=>setStageForm(f=>({...f,name_zh:e.target.value}))} style={{flex:1,fontSize:12,padding:"3px 7px",border:"1px solid rgba(212,175,55,0.3)",borderRadius:5,background:"#fff"}}/>
                ):(
                  <span style={{flex:1,fontSize:12,color:TXT}}>{s.name_zh}</span>
                )}
                {s.credential_key&&<span style={{fontSize:10,color:"#888",background:"rgba(0,0,0,0.04)",borderRadius:4,padding:"1px 6px"}}>{s.credential_key}</span>}
                {s.is_required&&<span style={{fontSize:10,color:GD,background:"rgba(139,105,20,0.08)",borderRadius:4,padding:"1px 6px"}}>必修</span>}
                <div style={{display:"flex",gap:3}}>
                  {si>0&&<button onClick={async()=>{const ids=(t.stages||[]).map(x=>x.id);const tmp=ids[si];ids[si]=ids[si-1];ids[si-1]=tmp;await fetch(`${API}/vajra/staff/teachings/${t.id}/stages/reorder`,{method:"PUT",headers:{...headers,"Content-Type":"application/json"},body:JSON.stringify({order:ids})});loadTeachings();}} style={{fontSize:11,padding:"1px 5px",borderRadius:4,cursor:"pointer",background:"none",border:"1px solid #ddd",color:"#666"}}>↑</button>}
                  {si<(t.stages||[]).length-1&&<button onClick={async()=>{const ids=(t.stages||[]).map(x=>x.id);const tmp=ids[si];ids[si]=ids[si+1];ids[si+1]=tmp;await fetch(`${API}/vajra/staff/teachings/${t.id}/stages/reorder`,{method:"PUT",headers:{...headers,"Content-Type":"application/json"},body:JSON.stringify({order:ids})});loadTeachings();}} style={{fontSize:11,padding:"1px 5px",borderRadius:4,cursor:"pointer",background:"none",border:"1px solid #ddd",color:"#666"}}>↓</button>}
                  {editStageId===s.id?(
                    <button onClick={async()=>{const r=await fetch(`${API}/vajra/staff/stages/${s.id}`,{method:"PUT",headers:{...headers,"Content-Type":"application/json"},body:JSON.stringify(stageForm)});if(r.ok){setStageMsg("✓ 已保存");setEditStageId(null);loadTeachings();}else{setStageMsg("✗ 失败");}}} style={{fontSize:11,padding:"1px 7px",borderRadius:4,cursor:"pointer",background:"rgba(5,150,105,0.08)",border:"1px solid rgba(5,150,105,0.25)",color:"#059669"}}>保存</button>
                  ):(
                    <button onClick={()=>{setEditStageId(s.id);setStageForm({stage_order:s.stage_order,name_zh:s.name_zh,name_en:s.name_en||"",description_zh:s.description_zh||"",credential_key:s.credential_key||"",is_required:s.is_required});}} style={{fontSize:11,padding:"1px 5px",borderRadius:4,cursor:"pointer",background:"none",border:"1px solid #ddd",color:"#888"}}>✎</button>
                  )}
                  <button onClick={async()=>{if(!window.confirm(`删除阶段"${s.name_zh}"？`))return;const r=await fetch(`${API}/vajra/staff/stages/${s.id}`,{method:"DELETE",headers});if(r.ok){setStageMsg("✓ 已删除");loadTeachings();}}} style={{fontSize:11,padding:"1px 5px",borderRadius:4,cursor:"pointer",background:"none",border:"1px solid #fca5a5",color:"#dc2626"}}>×</button>
                </div>
              </div>
            ))}
            {activeStageTeachingId===t.id&&(
              <div style={{marginTop:8,padding:"10px 12px",background:"rgba(212,175,55,0.04)",borderRadius:8,border:"1px solid rgba(212,175,55,0.15)"}}>
                <div style={{fontSize:11,color:GD,fontWeight:600,marginBottom:6}}>添加阶段</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
                  {[["name_zh","阶段名称（中文）*"],["name_en","阶段名称（英文）"],["credential_key","资质 Key（选填）"],["stage_order","排序序号"]].map(([k,label])=>(
                    <input key={k} placeholder={label} value={stageForm[k]||""} onChange={e=>setStageForm(f=>({...f,[k]:k==="stage_order"?parseInt(e.target.value)||1:e.target.value}))} style={{padding:"5px 8px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:6,fontSize:12,background:"#fff"}}/>
                  ))}
                </div>
                <textarea placeholder="简介（选填）" value={stageForm.description_zh||""} onChange={e=>setStageForm(f=>({...f,description_zh:e.target.value}))} rows={2} style={{width:"100%",padding:"5px 8px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:6,fontSize:12,background:"#fff",marginBottom:6,boxSizing:"border-box",resize:"vertical"}}/>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <label style={{fontSize:12,color:TXT2,display:"flex",alignItems:"center",gap:4,cursor:"pointer"}}>
                    <input type="checkbox" checked={stageForm.is_required} onChange={e=>setStageForm(f=>({...f,is_required:e.target.checked}))}/> 必修
                  </label>
                  <button onClick={async()=>{if(!stageForm.name_zh.trim()){setStageMsg("✗ 请填写阶段名称");return;}const r=await fetch(`${API}/vajra/staff/teachings/${t.id}/stages`,{method:"POST",headers:{...headers,"Content-Type":"application/json"},body:JSON.stringify(stageForm)});if(r.ok){setStageMsg("✓ 阶段已添加");setActiveStageTeachingId(null);setStageForm(BLANK_STAGE);loadTeachings();}else{setStageMsg("✗ 添加失败");}}} style={{padding:"4px 14px",borderRadius:8,cursor:"pointer",background:GD,color:"#fff",border:"none",fontSize:12}}>保存</button>
                  <button onClick={()=>{setActiveStageTeachingId(null);setStageMsg("");}} style={{padding:"4px 10px",borderRadius:8,cursor:"pointer",background:"none",border:"1px solid #ddd",color:"#888",fontSize:12}}>取消</button>
                </div>
              </div>
            )}
          </div>

          {/* Phase 72: Resource management — inside each teaching card */}
          {resourceActions(t.id)}
        </div>
      ))}
    </div>
  );
}
